"use client";
import { useState } from "react";
import {
  Check, User, Phone, Mail, BookOpen, Calendar,
  Loader2, CheckCircle2, AlertCircle
} from "lucide-react";
import { courses } from "../../lib/courses";
import Link from "next/link";

// (framer-motion removed) — basic static rendering now.

// Batch options
const batches = [
  { id: "batch-dec-2025", name: "December 2025 Batch 3", filledspots: 15, leftspots: 5, },
  { id: "batch-jan-2026", name: "February 2026 Batch 4", filledspots: 4, leftspots: 16 }
];

// 🔹 Reusable Input Component
function InputField({ id, label, icon: Icon, type = "text", value, onChange, error, placeholder, disabled }) {
  return (
    <div>
      <label htmlFor={id} className="flex items-center gap-2 text-sm font-semibold text-[var(--foreground)] mb-3">
        <Icon className="w-4 h-4" />
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-xl border border-[var(--blue-border)] bg-[rgba(255,255,255,0.03)] px-4 py-3 text-white/90 placeholder:text-white/40 focus:border-[var(--blue-accent)] focus:outline-none focus:ring-2 focus:ring-[color-mix(in_srgb,var(--blue-accent)_35%,transparent)] transition-colors"
      />
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

// 🔹 Reusable Select Component
function SelectField({ id, label, icon: Icon, value, onChange, options, error, placeholder, disabled }) {
  return (
    <div>
      <label htmlFor={id} className="flex items-center gap-2 text-sm font-semibold text-[var(--foreground)] mb-3">
        <Icon className="w-4 h-4" />
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full rounded-xl border border-[var(--blue-border)] bg-[rgba(255,255,255,0.03)] px-4 py-3 text-white/90 focus:border-[var(--blue-accent)] focus:outline-none focus:ring-2 focus:ring-[color-mix(in_srgb,var(--blue-accent)_35%,transparent)] transition-colors"
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} className="bg-[#1a1a1a] text-white/90">
            {opt.label}
          </option>
        ))}
      </select>
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

export default function EnrollmentForm() {
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", course: "", batch: "", agreedToTerms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // success | error | null
  const [errors, setErrors] = useState({});

  // 🔹 Validation (centralized)
  const validateForm = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = "Name is required";
    else if (formData.name.trim().length < 2) errs.name = "Must be at least 2 characters";

    if (!formData.phone.trim()) errs.phone = "Phone is required";
    else if (!/^\+?[\d\s\-\(\)]{10,}$/.test(formData.phone)) errs.phone = "Invalid phone number";

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = "Invalid email";

    if (!formData.course) errs.course = "Select a course";
    if (!formData.batch) errs.batch = "Select a batch";
    if (!formData.agreedToTerms) errs.terms = "You must agree";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // 🔹 Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const res = await fetch("/api/enroll", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setSubmitStatus("success");
        setFormData({ name: "", phone: "", email: "", course: "", batch: "", agreedToTerms: false });
      } else setSubmitStatus("error");
    } catch {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative" style={{ background: 'var(--blue-bg)' }}>
      {/* Background */}
      <div aria-hidden className="pointer-events-none fixed inset-0 overflow-hidden">
        {/* Soft grid for depth */}
        <div className="absolute inset-0 opacity-[0.15] bg-[linear-gradient(90deg,rgba(255,255,255,0.3)_1px,transparent_1px),linear-gradient(180deg,rgba(255,255,255,0.3)_1px,transparent_1px)] bg-[size:80px_80px]" />
        {/* Ambient blue orbs */}
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-[radial-gradient(closest-side,rgba(53,64,114,0.42),transparent_70%)] blur-2xl opacity-60" />
        <div className="absolute top-1/4 -right-48 w-[600px] h-[600px] rotate-45 rounded-full bg-[conic-gradient(from_160deg,rgba(42,51,90,0.55),transparent_60%)] blur-3xl opacity-50" />
        <div className="absolute bottom-[-15%] -left-24 w-[640px] h-[640px] rounded-full bg-[radial-gradient(circle_at_center,rgba(53,64,114,0.35),transparent_60%)] blur-[60px] opacity-50" />
        {/* Vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_70%,rgba(0,0,0,0.55))]" />
      </div>

      <div className="relative pt-20 pb-16 px-6 md:px-10 max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-5xl font-semibold tracking-tight text-[#ee4723] mb-4">Enroll Now</h1>
          <p className="text-base md:text-lg text-[var(--foreground)]/80">Secure your spot today - Limited seats available</p>
        </div>

        {/* Messages */}
        {submitStatus === "success" && (
          <Message type="success" title="Enrollment Submitted!" text="You have registered successfully. We will confirm via WhatsApp shortly." />
        )}
        {submitStatus === "error" && (
          <Message type="error" title="Submission Failed" text="Something went wrong. Please try again." />
        )}

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="relative overflow-hidden space-y-6 rounded-3xl border p-8 md:p-10 backdrop-blur-xl"
          style={{
            borderColor: 'color-mix(in srgb, var(--blue-border) 70%, transparent)',
            background: 'linear-gradient(140deg, rgba(31,38,67,0.75), rgba(31,38,67,0.45))'
          }}
        >
          {/* Inner glass highlights */}
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-10 left-1/3 w-72 h-72 rounded-full blur-3xl opacity-25" style={{ background: 'radial-gradient(circle at center, rgba(53,64,114,0.65), transparent 60%)' }} />
            <div className="absolute bottom-[-2rem] right-[-3rem] w-[22rem] h-[22rem] rounded-full blur-3xl opacity-20" style={{ background: 'conic-gradient(from 150deg, rgba(42,51,90,0.6), transparent 60%)' }} />
            {/* Sheen */}
            <div className="absolute -top-1 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
          </div>
          {/* Inputs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <InputField id="name" label="Full Name *" icon={User} value={formData.name}
              onChange={(v) => setFormData((f) => ({ ...f, name: v }))} error={errors.name}
              placeholder="Enter your full name" disabled={isSubmitting} />
            <InputField id="phone" label="Whatsapp Number *" icon={Phone} type="tel" value={formData.phone}
              onChange={(v) => setFormData((f) => ({ ...f, phone: v }))} error={errors.phone}
              placeholder="Enter Whatsapp Number" disabled={isSubmitting} />
          </div>

          <InputField id="email" label="Email (Optional)" icon={Mail} type="email" value={formData.email}
            onChange={(v) => setFormData((f) => ({ ...f, email: v }))} error={errors.email}
            placeholder="Enter your email" disabled={isSubmitting} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SelectField id="course" label="Select Course *" icon={BookOpen}
              value={formData.course} onChange={(v) => setFormData((f) => ({ ...f, course: v }))}
              options={courses.map((c) => ({ value: c.slug, label: `${c.title} (${c.duration})` }))}
              placeholder="Choose a course..." error={errors.course} disabled={isSubmitting} />

            <SelectField id="batch" label="Select Batch *" icon={Calendar}
              value={formData.batch} onChange={(v) => setFormData((f) => ({ ...f, batch: v }))}
              options={batches.map((b) => ({ value: b.id, label: `${b.name} (${b.leftspots} spots left) (${b.filledspots} filled)`  }))}
              placeholder="Choose a batch..." error={errors.batch} disabled={isSubmitting} />
          </div>

          {/* Terms */}
          <div>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input type="checkbox" checked={formData.agreedToTerms}
                onChange={(e) => setFormData((f) => ({ ...f, agreedToTerms: e.target.checked }))}
                className="sr-only" disabled={isSubmitting} />
              <div className={`relative w-5 h-5 rounded border-2 transition-all duration-200 ${
                formData.agreedToTerms
                  ? "border-[var(--blue-accent)] bg-[var(--blue-accent)]"
                  : "border-white/30 group-hover:border-white/60"
              }`}>
                {formData.agreedToTerms && <Check className="w-3 h-3 text-white absolute top-0.5 left-0.5" strokeWidth={3} />}
              </div>
              <span className="text-sm text-white/90 leading-relaxed">
                I agree to the <Link href="/terms" className="text-brand underline">Terms and conditions</Link> *
              </span>
            </label>
            {errors.terms && <p className="mt-1 text-xs text-red-400">{errors.terms}</p>}
          </div>

          {/* Submit */}
            <button type="submit" disabled={isSubmitting}
              className="w-full rounded-xl flex justify-center items-center gap-3 cursor-pointer bg-brand hover:brightness-110 disabled:opacity-50 px-6 py-4 font-semibold text-white transition-colors focus:ring-2 focus:ring-brand/40">
            {isSubmitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</> : "Submit Enrollment"}
          </button>
            {/* Ambient blue shapes inside the form (keeps original pattern, blue variant) */}
            <div aria-hidden className="pointer-events-none absolute inset-0">
              <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full blur-3xl opacity-25" style={{ background: 'radial-gradient(circle at center, rgba(53,64,114,0.55), transparent 60%)' }} />
              <div className="absolute -bottom-20 -right-24 w-[28rem] h-[28rem] rounded-full blur-3xl opacity-20 rotate-12" style={{ background: 'conic-gradient(from 180deg, rgba(42,51,90,0.55), transparent 60%)' }} />
            </div>
  </form>
      </div>
    </div>
  );
}

// 🔹 Message Component
function Message({ type, title, text }) {
  const isSuccess = type === "success";
  return (
    <div className={`mb-8 rounded-2xl border p-6 backdrop-blur-sm transition ${isSuccess ? "border-green-500/25 bg-green-500/10" : "border-red-500/25 bg-red-500/10"}`}>
      <div className="flex items-center gap-3">
        {isSuccess ? <CheckCircle2 className="w-6 h-6 text-green-400" /> : <AlertCircle className="w-6 h-6 text-red-400" />}
        <div>
          <h3 className={`font-semibold mb-1 ${isSuccess ? "text-green-400" : "text-red-400"}`}>{title}</h3>
          <p className="text-sm text-brand">{text}</p>
        </div>
      </div>
    </div>
  );
}